import { Box, Stack } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useTu } from "@src/appV2/i18n/useTu";
import { Button } from "@src/appV2/redesign/components/Button";
import { COACHMARK_Z_INDEX } from "@src/appV2/redesign/components/Coachmark/constants";
import { InlineAlert } from "@src/appV2/redesign/components/InlineAlert/InlineAlert";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { ModalsContainerProvider } from "@src/appV2/redesign/components/useModalsContainerContext";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import { ModalStatus, type UseModalState } from "@src/lib/ui-react";
import { type ReactElement } from "react";

interface RememberEmailDialogProps {
  open: boolean;
  /** The email address the user just signed in with. */
  email: string;
  /** Remember the email on this device. */
  onRemember: () => void;
  /** Don't remember this time (e.g. a shared computer). */
  onDecline: () => void;
  /** Never ask again on this device. */
  onSuppress: () => void;
}

/**
 * Post-sign-in prompt asking whether to remember the email on this device so
 * it's pre-filled next time. Includes a shared-computer warning (DEVOP-5493).
 *
 * Self-wraps in `berlinRebrandTheme` (matching the login views) so it renders
 * correctly even when mounted outside a themed subtree, e.g. at the AuthRedirect
 * root.
 */
export function RememberEmailDialog({
  open,
  email,
  onRemember,
  onDecline,
  onSuppress,
}: RememberEmailDialogProps): ReactElement {
  const { tu } = useTu("login");

  // Minimal modal state driven by the `open` prop; closing (backdrop / Escape /
  // X) is treated as declining so the sign-in flow always resumes.
  const modalState: UseModalState = {
    modalStatus: open ? ModalStatus.OPEN : ModalStatus.CLOSED,
    setModalStatus: () => undefined,
    modalIsOpen: open,
    modalIsClosed: !open,
    openModal: () => undefined,
    closeModal: onDecline,
  };

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <ModalsContainerProvider>
        <ResponsiveDialog
          modalState={modalState}
          title={tu("Remember your email on this device?")}
          onClose={onDecline}
          // Render above onboarding coachmarks (snackbar-level z-index) so the
          // prompt isn't pierced by a tooltip layered on top of it.
          sx={{ zIndex: COACHMARK_Z_INDEX + 1 }}
          footerContent={
            <Box
              sx={(theme) => ({
                paddingX: theme.spacing(5),
                paddingY: theme.spacing(3),
              })}
            >
              <Stack spacing={3}>
                <Button fullWidth variant="primary" onClick={onRemember}>
                  {tu("Remember email")}
                </Button>
                <Button fullWidth variant="outlined" onClick={onDecline}>
                  {tu("Not now (but ask again upon next login)")}
                </Button>
                <Button fullWidth variant="outlined" onClick={onSuppress}>
                  {tu("Don't ask again on this computer")}
                </Button>
              </Stack>
            </Box>
          }
        >
          <Stack spacing={4}>
            <Text variant="body1" sx={{ color: "base.muted" }}>
              {tu(
                "We'll pre-fill {{email}} next time you sign in so you don't have to type it again.",
                { values: { email } }
              )}
            </Text>
            <InlineAlert
              message={tu(
                "Don't do this on a shared or public computer — anyone using it could see your email."
              )}
            />
          </Stack>
        </ResponsiveDialog>
      </ModalsContainerProvider>
    </ThemeProvider>
  );
}
